import React from 'react'
import { DateTimeV2, Truncate, VerticalDivider } from '@labrav/react-components'
import { CloudMarketplace } from '@labrav/react-components/lib/@types/types/forms'
import { OfferStatusChip } from '../../../common/components/StatusChips'
import { OfferStatus as OfferStatusEnum } from '../../../modules/privateOffer/reducer'
import { useStyles } from './OfferCard.styles'
import { CardBox } from '../CardBox/CardBox'
import { getCurrencySymbol } from '@labrav/common'

interface Installments {
  payment_date: string
  amount?: number | null
}

export interface Offer {
  azure_private_offer_id?: string
  cloud_account_id?: string
  labra_offer_id?: string
  aws_offer_id?: string
  azure_offer_id?: string
  gcp_offer_id?: string
  id?: string
  private_offer_id?: string
  cloud?: string
  private_offer_name?: string
  name?: string
  company_name?: string
  cloud_offer_id?: string
  labra_offer_url?: string
  cloud_offer_url?: string
  links?: { self?: string }
  created_at?: string
  updated_at?: string
  offer_expiration_date?: string
  end?: string
  product_name?: string
  product_id?: string
  offer_status?: string
  error?: string
  labra_product_id?: string
  aws_product_id?: string
  azure_product_id?: string
  gcp_product_id?: string
  draft?: boolean
  installments?: Installments[]
  flexible_payment_terms?: boolean
  entitlement_option?: string
  dimensions?: Record<string, any>[]
  currency?: string
  acceptance_link?: string
}

export interface OfferCardProps {
  offer: Offer
  index: number
  isCollapsed: boolean
  onToggleCollapse: () => void
  onOfferClick?: (
    offerId: string,
    productId: string,
    draft: boolean,
    cloudAccountId: string,
    cloudMarketplace: CloudMarketplace
  ) => void
}

export const OfferCard: React.FC<OfferCardProps> = ({
  offer,
  index,
  isCollapsed,
  onToggleCollapse,
  onOfferClick,
}) => {
  const { classes } = useStyles()
  const offerId =
    offer.labra_offer_id ||
    offer.id ||
    offer.private_offer_id ||
    `offer-${index}`
  const cloudType = offer.cloud
  const offerName = offer.private_offer_name || offer.name || 'Private Offer'
  const companyName = offer.company_name || ''
  const cloudOfferId =
    cloudType === 'AWS'
      ? offer.aws_offer_id
      : cloudType === 'AZURE'
        ? offer?.azure_private_offer_id
        : cloudType === 'GCP'
          ? offer.cloud_offer_id
          : ''
  const publishedDate = offer.created_at || offer.updated_at
  const expiryDate = offer.offer_expiration_date || offer.end
  const isExpired = expiryDate && new Date(expiryDate) < new Date()

  const productId =
    cloudType === 'AWS'
      ? offer.aws_product_id
      : cloudType === 'AZURE'
        ? offer.labra_product_id || ''
        : cloudType === 'GCP'
          ? offer.labra_product_id || ''
          : ''

  const isDraft =
    cloudType === 'AWS'
      ? !!offer.draft
      : cloudType === 'AZURE'
        ? offer.offer_status === OfferStatusEnum.OFFER_IN_DRAFT
        : !!(
            offer?.draft ||
            offer.offer_status === OfferStatusEnum.OFFER_IN_DRAFT ||
            offer.offer_status === OfferStatusEnum.PRIVATE_OFFER_IN_DRAFT
          )
  // Bubble up the click event to parent component
  const handleOfferClick = (offerId: string) => {
    if (onOfferClick && offerId && productId) {
      const cloudAccountId = offer?.cloud_account_id || ''
      const cloudMarketplace = offer?.cloud as CloudMarketplace
      onOfferClick(
        offerId,
        productId,
        isDraft,
        cloudAccountId,
        cloudMarketplace
      )
    }
  }

  // Get the proper cloud name for the label
  const getCloudOfferIdLabel = (cloud: string): string => {
    const cloudUpper = cloud.toUpperCase()
    if (cloudUpper === 'AWS') return 'AWS offer ID'
    if (cloudUpper === 'AZURE') return 'Microsoft offer ID'
    if (cloudUpper === 'GCP' || cloudUpper === 'GOOGLE') return 'GCP offer ID'
    return `${cloud} offer ID`
  }

  const tcv = getTcvValue(offer)

  const isTcvAvailable =
    offer?.flexible_payment_terms ||
    (offer?.entitlement_option === 'SINGLE' &&
      offer?.dimensions &&
      offer?.dimensions.length === 1)

  const currencySymbol = getCurrencySymbol(offer?.currency || 'USD')

  const offerLink = isDraft
    ? undefined
    : cloudType === 'AZURE'
      ? offer.acceptance_link
      : cloudType === 'AWS' || cloudType === 'GCP'
        ? offer.cloud_offer_url
        : ''
  return (
    <CardBox
      id={offerId}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
      onCardClick={() => handleOfferClick(offerId)}
      copyLinkUrl={offerLink}
      cloudType={cloudType}
      title={offerName}
      viewLink={!isDraft}
      content={
        <>
          <div className={classes.cardGrid}>
            <div className={classes.gridItem}>
              <div className={classes.metaLabel}>Company name</div>
              <div className={classes.metaValue}>
                <Truncate width={200} clipboardTooltip>
                  {companyName || '-'}
                </Truncate>
              </div>
            </div>
            <div className={classes.gridItem}>
              <div className={classes.metaLabel}>
                {getCloudOfferIdLabel(cloudType || '')}
              </div>
              <div className={classes.metaValue}>
                <Truncate width={'80%'} clipboardTooltip>
                  {cloudOfferId || '-'}
                </Truncate>
              </div>
            </div>
            <div className={classes.gridItem}>
              <div className={classes.metaLabel}>Published on</div>
              <div className={classes.metaValue}>
                {publishedDate ? (
                  <DateTimeV2 variant="formattedOnly" date={publishedDate} />
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className={classes.gridItem}>
              <div className={classes.metaLabel}>Expiry date</div>
              <div
                className={`${classes.metaValue} ${classes.expiryDateValue}`}
              >
                {expiryDate ? (
                  <>
                    <DateTimeV2 variant="formattedOnly" date={expiryDate} />
                    {isExpired && (
                      <span className={classes.expiredText}>(Expired)</span>
                    )}
                  </>
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>
        </>
      }
      headerContent={
        <>
          <div className={classes.headerContent}>
            {offer.offer_status && (
              <div className={classes.statusRow}>
                <div className={classes.statusItem}>
                  <OfferStatusChip
                    status={
                      isDraft
                        ? OfferStatusEnum.OFFER_IN_DRAFT
                        : offer.offer_status
                    }
                    cloud={cloudType as CloudMarketplace}
                  />
                </div>
              </div>
            )}
            {!!isTcvAvailable && (
              <>
                <VerticalDivider height="24px" />
                <div className={classes.tcvText}>
                  <span className={classes.tcvLabel}>
                    TCV: {currencySymbol}
                  </span>
                  <span className={classes.tcvLabel}>{tcv}</span>
                </div>
              </>
            )}
          </div>
        </>
      }
    />
  )
}

const getTcvValue = (offer: Offer) => {
  if (offer.flexible_payment_terms) {
    return offer.installments?.reduce((acc, installment) => {
      return acc + (installment.amount || 0)
    }, 0)
  }

  if (
    offer?.entitlement_option === 'SINGLE' &&
    offer?.dimensions &&
    offer?.dimensions.length === 1
  ) {
    return offer?.dimensions[0]?.price
  }
  return 0
}
